import C from "@/modules/billing/forms/BillingSelect.module.css";

import type { BillingAccountCardWithData } from "@/modules/billing/billingCardsQuery.ts";
import { getCardIconClass, getMethodLinkIconClass } from "@/modules/billing/iconClasses.tsx";
import type { PaymentMethod, PaymentMethodId } from "@/modules/billing/paymentMethods.ts";
import { byCardGateway } from "@/modules/billing/utils.tsx";
import { mcn } from "@/utils/baseProps.ts";
import { Trans } from "@lingui/react/macro";
import type { BillingAccountCardData } from "@warrenio/api-spec/spec.oats.gen";

//#region Icons

function getPaymentMethodIconClass(card: BillingAccountCardWithData) {
    return getCardIconClass(card.parsedAdditionalData);
}

export function PaymentMethodIcon({ card, ...props }: { card: BillingAccountCardWithData }) {
    return <span {...mcn(`${C.MethodIcon} inline-block ${getPaymentMethodIconClass(card)}`, props)} />;
}

export function PaymentMethodTitle({
    method: { id, name },
    card,
    showGateway = false,
}: {
    method: PaymentMethod;
    card?: BillingAccountCardWithData;
    showGateway: boolean;
}) {
    const gatewayNameTrans = showGateway && byCardGateway({ methodId: id });

    return (
        <div>
            {card ? <CardShortInfo card={card} /> : name}
            {gatewayNameTrans && <i className="color-muted"> {gatewayNameTrans}</i>}
        </div>
    );
}

export interface PaymentLinkIconProps {
    icon: PaymentMethodId;
    isSelected?: boolean;
}

export function PaymentLinkIcon({ icon, isSelected = false, ...props }: PaymentLinkIconProps) {
    return <span {...mcn(`${C.MethodIcon} inline-block ${getMethodLinkIconClass(icon, isSelected)}`, props)} />;
}

//#endregion

export type CardShortInfoData = Pick<
    BillingAccountCardData,
    "card_type" | "brand" | "type" | "last4" | "expire_month" | "expire_year" | "card_holder"
>;

/** In top up selector and BA detail view */
export function CardShortInfo({ card }: { card: BillingAccountCardWithData }) {
    return <PrimaryCardShortInfo card={card.parsedAdditionalData} />;
}

function PrimaryCardShortInfo({ card }: { card: CardShortInfoData }) {
    const { type, card_type } = card;
    if (type === "paying_by_invoice") {
        return <Trans>Paying by invoice</Trans>;
    } else if (card_type === "PayPal") {
        const { card_holder } = card;
        return card_holder;
    } else {
        const { last4, expire_month, expire_year, card_type, type } = card;
        const brandText = card_type || type;
        return (
            <>
                <span className="text-capitalize">{last4 ? `${brandText}*${last4}` : brandText}</span>
                <span className="color-muted">
                    {" "}
                    ({expire_month}/{expire_year})
                </span>
            </>
        );
    }
}

/** In Billing Account List view */
export function PrimaryCardBlockInfo({ card }: { card: CardShortInfoData }) {
    const { last4, expire_month, expire_year, card_type, type } = card;
    const brandText = card_type || type;
    return (
        <span>
            <span>{last4 ? `*${last4}` : brandText}</span>
            <span>
                , {expire_month}/{expire_year}
            </span>
        </span>
    );
}
